import { Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { StackedAvatars } from '../../ui-kit'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug } from './notificationNav'
import { ActorNameSpan } from './notificationTypography'
import { IconLikeFilled } from '../../../icons/IconLikeFilled'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.like.summary' }>
}

export function ProjectLikeSummaryNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onProjectClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload: any = (notification as any).payload ?? {}

  const project = payload.project ?? {}
  const projectTitle = typeof project.title === 'string' && project.title.trim().length > 0 ? project.title : 'проект'
  const projectAvatarSrc = mediaSourceUrl(project.image_url) ?? undefined
  const projectSlug = getProjectSlug(project)

  const likesCount = Number(payload.likes_count ?? 0)
  const lastActor = payload.last_actor ?? {}
  const lastActorName =
    typeof lastActor.fullname === 'string' && lastActor.fullname.trim().length > 0 ? lastActor.fullname : 'Пользователь'
  const actorPathKey = getActorPathKey(lastActor, notification.actor_user_id)

  const more = Math.max(likesCount - 1, 0)
  const actorsPreviewRaw = Array.isArray(payload.actors_preview) ? payload.actors_preview : []
  const actorsPreview = actorsPreviewRaw.map((u: any) => ({ ...u, avatar: u.avatar ?? u.avatar_url ?? null }))

  const likeSuffix =
    more > 0 ? ` и ещё ${more} поставили лайк вашему проекту` : ' поставил лайк вашему проекту'

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Лайки проекта"
            avatarSize={38}
          />
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} minWidth={0}>
          <Stack minWidth={0} gap={0.5}>
            <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
              <StackedAvatars users={actorsPreview} size={24} overlap={8} />
              <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {lastActorName}
                </ActorNameSpan>
                {likeSuffix}
              </Typography>
            </Stack>
          </Stack>

          
          <Stack direction="row" alignItems="center" gap={likesCount > 0 ? 1 : 0} sx={{ lineHeight: 1 }}>
            <IconLikeFilled fontSize="small" />
            {likesCount > 0 && <span style={{ 
              userSelect: 'none', 
              fontWeight: 500,
              fontSize: '14px' 
            }}>
              {likesCount}
            </span>}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
